import React, { useState } from 'react';
import Cookies from 'js-cookie';
import { loginUser, registerUser } from '../api'; // Import the login and register API functions
import '../styles/LoginPopup.css'; // Import the CSS file for styling

const LoginPopup = ({ onLogin }) => {
  const [isRegistering, setIsRegistering] = useState(false); // Toggle between login and register forms
  const [isForgotPassword, setIsForgotPassword] = useState(false); // Toggle for forgot password form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const handleLogin = async () => {
    setErrors({});
    
    // Validate inputs
    const loginErrors = {};
    if (!username.trim()) {
      loginErrors.username = 'Username is required';
    }
    if (!password.trim()) {
      loginErrors.password = 'Password is required';
    }
    
    if (Object.keys(loginErrors).length > 0) {
      setErrors(loginErrors);
      return;
    }

    try {
      const response = await loginUser(username, password); // Call the login API
      const { access_token, refresh_token, token_type } = response;

      // Store tokens in cookies
      Cookies.set('access_token', `${token_type} ${access_token}`, {
        expires: 30 / (24 * 60),
        path: '/', 
      }); // Set expiry to 25 minutes
      Cookies.set('refresh_token', `${token_type} ${refresh_token}`, { expires: 120 / (24 * 60) }); // Set expiry to 55 minutes

      onLogin(); // Call the onLogin callback to indicate successful login
    } catch (err) {
      setErrors({ general: 'Invalid username or password' }); // Handle login error
    }
  };

  const handleRegister = async () => {
    setErrors({});
    
    // Validate inputs
    const registerErrors = {};
    if (!username.trim()) {
      registerErrors.username = 'Username is required';
    }
    if (!email.trim()) {
      registerErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      registerErrors.email = 'Please enter a valid email address';
    }
    if (!fullName.trim()) {
      registerErrors.fullName = 'Full name is required';
    }
    if (!password.trim()) {
      registerErrors.password = 'Password is required';
    } else if (password.length < 8) {
      registerErrors.password = 'Password must be at least 8 characters long';
    }
    
    if (Object.keys(registerErrors).length > 0) {
      setErrors(registerErrors);
      return;
    }

    try {
      await registerUser(username, email, fullName, password); // Call the register API
      setIsRegistering(false); // Switch back to login form after successful registration
      setErrors({}); // Clear any errors
      alert('Registration successful! Please log in.');
    } catch (err) {
      setErrors({ general: 'Failed to register. Please check your details and try again.' }); // Handle registration error
    }
  };

  const handleForgotPassword = async () => {
    setErrors({});
    setSuccessMessage('');

    // Validate inputs
    const forgotPasswordErrors = {};
    if (!username.trim()) {
      forgotPasswordErrors.username = 'Username is required';
    }
    if (!newPassword.trim()) {
      forgotPasswordErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      forgotPasswordErrors.newPassword = 'Password must be at least 8 characters long';
    }
    if (!confirmPassword.trim()) {
      forgotPasswordErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      forgotPasswordErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (Object.keys(forgotPasswordErrors).length > 0) {
      setErrors(forgotPasswordErrors);
      return;
    }

    try {
      // TODO: Implement forgot password API call
      // await resetPassword(username, newPassword);
      console.log('Password reset for:', username);
      setSuccessMessage('Password reset successful! Please log in with your new password.');
      
      // Reset form and switch back to login after 2 seconds
      setTimeout(() => {
        setIsForgotPassword(false);
        setUsername('');
        setNewPassword('');
        setConfirmPassword('');
        setSuccessMessage('');
      }, 2000);
    } catch (err) {
      setErrors({ general: 'Failed to reset password. Please try again or contact support.' });
    }
  };

  const handleBackToLogin = () => {
    setIsForgotPassword(false);
    setIsRegistering(false);
    setUsername('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setEmail('');
    setFullName('');
    setErrors({});
    setSuccessMessage('');
  };

  return (
    <div className='login-popup'>
      <div className='login-popup-content'>
        {isForgotPassword ? (
          <>
            <h2>Reset Password</h2>
            <div className='input-group'>
              <input
                type='text'
                placeholder='Username'
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={errors.username ? 'input-error' : ''}
              />
              {errors.username && <p className='field-error'>{errors.username}</p>}
            </div>
            <div className='input-group'>
              <input
                type='password'
                placeholder='New Password'
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={errors.newPassword ? 'input-error' : ''}
              />
              {errors.newPassword && <p className='field-error'>{errors.newPassword}</p>}
            </div>
            <div className='input-group'>
              <input
                type='password'
                placeholder='Confirm New Password'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={errors.confirmPassword ? 'input-error' : ''}
              />
              {errors.confirmPassword && <p className='field-error'>{errors.confirmPassword}</p>}
            </div>
            {errors.general && <p className='error'>{errors.general}</p>}
            {successMessage && <p className='success'>{successMessage}</p>}
            <button onClick={handleForgotPassword}>Submit</button>
            <button onClick={handleBackToLogin} className='back-button'>
              Back to Login
            </button>
          </>
        ) : isRegistering ? (
          <>
            <h2>Register</h2>
            <div className='input-group'>
              <input
                type='text'
                placeholder='Username'
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={errors.username ? 'input-error' : ''}
              />
              {errors.username && <p className='field-error'>{errors.username}</p>}
            </div>
            <div className='input-group'>
              <input
                type='email'
                placeholder='Email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={errors.email ? 'input-error' : ''}
              />
              {errors.email && <p className='field-error'>{errors.email}</p>}
            </div>
            <div className='input-group'>
              <input
                type='text'
                placeholder='Full Name'
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={errors.fullName ? 'input-error' : ''}
              />
              {errors.fullName && <p className='field-error'>{errors.fullName}</p>}
            </div>
            <div className='input-group'>
              <input
                type='password'
                placeholder='Password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={errors.password ? 'input-error' : ''}
              />
              {errors.password && <p className='field-error'>{errors.password}</p>}
            </div>
            {errors.general && <p className='error'>{errors.general}</p>}
            <button onClick={handleRegister}>Register</button>
            <button onClick={handleBackToLogin} className='back-button'>
              Back to Login
            </button>
          </>
        ) : (
          <>
            <h2>Login</h2>
            <div className='input-group'>
              <input
                type='text'
                placeholder='Email address'
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={errors.email ? 'input-error' : ''}
              />
              {errors.username && <p className='field-error'>{errors.username}</p>}
            </div>
            <div className='input-group'>
              <input
                type='password'
                placeholder='Password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={errors.password ? 'input-error' : ''}
              />
              {errors.password && <p className='field-error'>{errors.password}</p>}
            </div>
            {errors.general && <p className='error'>{errors.general}</p>}
            <button onClick={handleLogin}>Login</button>
            <button 
              onClick={() => setIsForgotPassword(true)} 
              className='forgot-password-button'
            >
              Forgot Password?
            </button>
            {/* <button onClick={() => setIsRegistering(true)}>Register</button> */}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPopup;
